import { SVG_TO_PDF_SCALE } from "common/constants/pdfSettings";
import { TextFonts } from "common/constants/textFont";
import type { SheetPixels } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { numMul, sheetInchesToPixels, sheetPixelsToInches } from "common/utils/nominalNumbers";
import { DIST_EPSILON } from "geom/core";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3ExtractRotationAngleFrom, mat3ExtractScaleVectorFrom } from "geom/mat3";
import type { Vec2 } from "geom/vec2";
import { PDFTextMetricsProvider } from "pdf/pdfTextMetricsProvider";
import type { SVGParserCallbacks, SVGPathStyle } from "./svgParser";
import { SVGParser } from "./svgParser";
import { Path, ShapePath } from "./svgPathRuntime";
import type { SVGTextMetricsProvider } from "./svgTextMetricsProvider";

export interface LoadSVGResult {
  paths: ShapePath[];
  geomTypes: string[];
  xml: HTMLElement;
  height: SheetPixels | null;
  width: SheetPixels | null;
  viewBox: { x: SheetPixels; y: SheetPixels; width: SheetPixels; height: SheetPixels } | null;
}

export class SVGtoPDFTextMetricsProvider implements SVGTextMetricsProvider {
  heightOfString(text: string, fontSize: SheetPixels): SheetPixels {
    const fontSizeInches = sheetPixelsToInches(fontSize);
    const fontSizeScaled = numMul(fontSizeInches, SVG_TO_PDF_SCALE);
    const heightInches = PDFTextMetricsProvider.instance.heightOfString({
      text,
      fontSize: fontSizeScaled,
      font: TextFonts.ArialNarrow.id,
    });
    return sheetInchesToPixels(heightInches);
  }
  widthOfString(text: string, fontSize: SheetPixels): SheetPixels {
    const fontSizeInches = sheetPixelsToInches(fontSize);
    const fontSizeScaled = numMul(fontSizeInches, SVG_TO_PDF_SCALE);
    const widthInches = PDFTextMetricsProvider.instance.widthOfString({
      text,
      fontSize: fontSizeScaled,
      font: TextFonts.ArialNarrow.id,
    });
    return sheetInchesToPixels(widthInches);
  }
}

class SVGToThreeCallbacks implements SVGParserCallbacks<ShapePath, Path> {
  public viewBox: { x: SheetPixels; y: SheetPixels; width: SheetPixels; height: SheetPixels } | null = null;
  public svgWidth: SheetPixels | null = null;
  public svgHeight: SheetPixels | null = null;
  public paths: ShapePath[] = [];
  public geomTypes: string[] = [];
  constructor(
    private getTextAABB2: boolean = false,
    private textMetricsProvider: SVGTextMetricsProvider | null = null,
  ) {}

  reset(): void {
    // No allocation resetting of arrays.
    while (this.paths.length) {
      this.paths.pop();
    }
    while (this.geomTypes.length) {
      this.geomTypes.pop();
    }
  }

  // Basic SVG information from SVG tag
  setViewBox(x: SheetPixels, y: SheetPixels, width: SheetPixels, height: SheetPixels): void {
    this.viewBox = { x, y, width, height };
  }
  setSvgWidth(width: SheetPixels): void {
    this.svgWidth = width;
  }
  setSvgHeight(height: SheetPixels): void {
    this.svgHeight = height;
  }

  newShapePath(): ShapePath {
    return new ShapePath();
  }
  newPath(): Path {
    return new Path();
  }

  // ShapePath related operations

  shapePathMoveTo(path: ShapePath, transformation: Mat3, x: number, y: number): void {
    const p: Vec2 = mat3Dot(transformation, x, y);
    path.moveTo(p[0] as SheetPixels, p[1] as SheetPixels);
  }

  shapePathLineTo(path: ShapePath, transformation: Mat3, x: number, y: number): void {
    const p: Vec2 = mat3Dot(transformation, x, y);
    path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);
  }

  shapePathQuadraticCurveTo(
    path: ShapePath,
    transformation: Mat3,
    aCPx: SheetPixels,
    aCPy: SheetPixels,
    ax: SheetPixels,
    ay: SheetPixels,
  ): void {
    const [taCPx, taCPy] = mat3Dot(transformation, aCPx, aCPy);
    const [tax, tay] = mat3Dot(transformation, ax, ay);
    path.quadraticCurveTo(taCPx as SheetPixels, taCPy as SheetPixels, tax as SheetPixels, tay as SheetPixels);
  }

  shapePathBezierCurveTo(
    path: ShapePath,
    transformation: Mat3,
    aCP1x: SheetPixels,
    aCP1y: SheetPixels,
    aCP2x: SheetPixels,
    aCP2y: SheetPixels,
    ax: SheetPixels,
    ay: SheetPixels,
  ): void {
    const [taCP1x, taCP1y] = mat3Dot(transformation, aCP1x, aCP1y);
    const [taCP2x, taCP2y] = mat3Dot(transformation, aCP2x, aCP2y);
    const [tax, tay] = mat3Dot(transformation, ax, ay);
    path.bezierCurveTo(
      taCP1x as SheetPixels,
      taCP1y as SheetPixels,
      taCP2x as SheetPixels,
      taCP2y as SheetPixels,
      tax as SheetPixels,
      tay as SheetPixels,
    );
  }

  // `textAnchor` can be one of 'start', 'middle', 'end'
  // If null is returned from the callback the ShapePath is not added to the paths
  shapePathText(
    path: ShapePath,
    transformation: Mat3,
    text: string,
    _style: SVGPathStyle,
    x: SheetPixels,
    y: SheetPixels,
    fontSize: SheetPixels,
    textAnchor: string,
    baselineShift: string,
  ): ShapePath | null {
    if (!this.getTextAABB2 || !this.textMetricsProvider) {
      return null;
    }

    const w = this.textMetricsProvider.widthOfString(text, fontSize) as number;
    let h = this.textMetricsProvider.heightOfString(text, fontSize) as number;
    let right = w / 2;
    let left = -w / 2;

    switch (textAnchor) {
      case "start": {
        left = 0;
        right = w;
        break;
      }
      case "middle": {
        break;
      }
      case "end": {
        left = -w;
        right = 0;
        break;
      }
    }
    if (baselineShift) {
      const shift = parseFloat(baselineShift);
      if (!isNaN(shift)) {
        h -= (h * shift) / 100;
      }
    }
    let p = mat3Dot(transformation, x + left, y);
    path.moveTo(p[0] as SheetPixels, p[1] as SheetPixels);
    p = mat3Dot(transformation, x + right, y);
    path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);
    p = mat3Dot(transformation, x + right, y - h);
    path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);
    p = mat3Dot(transformation, x + left, y - h);
    path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);
    p = mat3Dot(transformation, x + left, y);
    path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);

    return path;
  }

  shapePathGetCurrentPath(path: ShapePath): Path {
    return ensureDefined(path.currentPath);
  }

  shapePathAddSubpath(path: ShapePath, subPath: Path): void {
    path.subPaths.push(subPath);
  }

  shapePathSetColorStyle(path: ShapePath, style: string): void {
    path.color.setStyle(style);
  }

  shapePathDone(path: ShapePath, node: HTMLElement, style: SVGPathStyle, _svgTransform: Mat3): void {
    path["userData"] = { node: node, style: style };
    if (style.fill !== undefined && style.fill !== "none") {
      if (style.fill.substring(0, 5) !== "url(#") {
        path.color.setStyle(style);
      }
    }
    this.paths.push(path);
    this.geomTypes.push(node.nodeName ?? "");
  }

  // Path related operations

  pathAutoClose(path: Path, close: boolean): void {
    path.autoClose = close;
  }

  pathCloseCurve(path: Path, point: Vec2, svgTransform: Mat3): void {
    const p: Vec2 = mat3Dot(svgTransform, point[0], point[1]);
    if (Math.abs(path.currentPoint.x - p[0]) > DIST_EPSILON || Math.abs(path.currentPoint.y - p[1]) > DIST_EPSILON) {
      path.lineTo(p[0] as SheetPixels, p[1] as SheetPixels);
    }
    path.currentPoint.set(p[0] as SheetPixels, p[1] as SheetPixels);
  }

  pathAbsEllipse(
    path: Path,
    transformation: Mat3,
    centerx: number,
    centery: number,
    xRadius: number,
    yRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise: boolean = false,
    aRotationRad: number = 0,
  ): void {
    const [tcx, tcy] = mat3Dot(transformation, centerx, centery);
    const xformScale = mat3ExtractScaleVectorFrom(transformation);
    const xformRotationRad = mat3ExtractRotationAngleFrom(transformation, xformScale) ?? 0;
    path.absellipse(
      tcx as SheetPixels,
      tcy as SheetPixels,
      (xformScale[0] * xRadius) as SheetPixels,
      Math.abs(xformScale[1] * yRadius) as SheetPixels,
      aStartAngleRad,
      aEndAngleRad,
      aClockwise,
      aRotationRad + xformRotationRad,
    );
  }

  pathAbsArc(
    path: Path,
    transformation: Mat3,
    centerx: number,
    centery: number,
    aRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise: boolean = false,
  ): void {
    const [tcx, tcy] = mat3Dot(transformation, centerx, centery);
    const xformScale = mat3ExtractScaleVectorFrom(transformation);
    const xformRotationRad = mat3ExtractRotationAngleFrom(transformation, xformScale) ?? 0;
    path.absarc(
      tcx as SheetPixels,
      tcy as SheetPixels,
      (xformScale[0] * aRadius) as SheetPixels,
      aStartAngleRad + xformRotationRad,
      aEndAngleRad + xformRotationRad,
      aClockwise,
    );
  }
}

export class SVGLoader {
  private callbacks: SVGToThreeCallbacks;
  constructor(
    _manager?: unknown,
    getTextAABB2: boolean = false,
    textMetricsProvider: SVGTextMetricsProvider | null = null,
  ) {
    this.callbacks = new SVGToThreeCallbacks(getTextAABB2, textMetricsProvider);
  }

  private static cache: LRUObjectCache<string, LoadSVGResult> = new LRUObjectCache(100, (a, b) => a === b);

  load(text: string): LoadSVGResult | null {
    if (SVGLoader.cache.has(text)) {
      return SVGLoader.cache.get(text) ?? null;
    }

    const svgParser: SVGParser<ShapePath, Path> = new SVGParser<ShapePath, Path>(this.callbacks);

    this.callbacks.reset();
    try {
      const xml = svgParser.parse(text);
      const data: LoadSVGResult = {
        xml,
        // shallow copy the paths and geomTypes as they are mutated by callbacks
        paths: this.callbacks.paths.slice(),
        geomTypes: this.callbacks.geomTypes.slice(),
        height: this.callbacks.svgHeight,
        width: this.callbacks.svgWidth,
        viewBox: this.callbacks.viewBox,
      };

      SVGLoader.cache.add(text, data);

      return data;
    } catch (e) {
      console.error(e);
    }
    return null;
  }
}
